/**
 * AI Assistant — the panel (header, message list, composer).
 * Presentational; all state lives in ApplicationsCopilot.jsx.
 *
 * G1: the header's context row shows the current page, the scope switcher
 * ("All jobs" / a job) and, in job scope, the applicant count + filters.
 *
 * Desktop: glass card 420×640 anchored above the launcher ("expanded" = 760
 * wide). <768 px: bottom sheet at 88vh with a grab handle and a focus trap.
 * Visual system: copilot.css (`cp-*`) + Tailwind.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import {
  X,
  Plus,
  History,
  Maximize2,
  Minimize2,
  Minus,
  Sparkles,
  Trophy,
  AlertTriangle,
  ListChecks,
  Clock,
  GitCompare,
  BarChart3,
  User,
  Search,
  Lock,
  WifiOff,
  ArrowDown,
  Hash,
  Quote,
  LayoutDashboard,
  Building2,
  CalendarDays,
  Coins,
  FileCheck,
  MessageSquare,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/Components/ui/tooltip';
import { cn } from '@/lib/utils';
import CopilotMessage from './CopilotMessage';
import CopilotAvatar from './CopilotAvatar';
import CopilotComposer from './CopilotComposer';
import CopilotHistory from './CopilotHistory';
import CopilotScopeSwitcher from './CopilotScopeSwitcher';

export const PLANS_PATH = '/hirewithus/plans';

const SUGGESTION_ICONS = {
  trophy: Trophy,
  alert: AlertTriangle,
  list: ListChecks,
  clock: Clock,
  compare: GitCompare,
  chart: BarChart3,
  user: User,
  search: Search,
  calendar: CalendarDays,
  credits: Coins,
  offer: FileCheck,
  message: MessageSquare,
};

/**
 * Page-aware starter chips (G2) shown while the backend suggestions are still
 * loading or came back empty. Overview / My Jobs / Applications keep the
 * server-provided list only.
 */
export const PAGE_FALLBACK_SUGGESTIONS = Object.freeze({
  'hiring-lifecycle': [
    { text: 'Which offers are pending acceptance?', icon: 'offer' },
    { text: 'Who is joining this month?', icon: 'calendar' },
  ],
  billing: [{ text: 'How many credits did we spend this month and on what?', icon: 'credits' }],
  messages: [{ text: 'Who replied on WhatsApp today?', icon: 'message' }],
  'hired-profiles': [{ text: 'How many hires this quarter and from which jobs?', icon: 'chart' }],
});

/** The chips to show: the backend list when it has entries, else the page fallback (or []). */
export function fallbackSuggestions(page, provided) {
  if (Array.isArray(provided) && provided.length) return provided;
  return PAGE_FALLBACK_SUGGESTIONS[page] || [];
}

const CAPABILITIES = [
  { icon: Hash, text: 'Counts & filters' },
  { icon: GitCompare, text: 'Compare candidates' },
  { icon: Quote, text: 'Quote resumes, transcripts & feedback' },
];

const COMPANY_CAPABILITIES = [
  { icon: Building2, text: 'Jobs, applicants & hires across the company' },
  { icon: Search, text: 'Find a candidate in any role' },
  { icon: LayoutDashboard, text: 'Open a job or a profile for you' },
];

const STATUS_LABEL = { ok: 'Online', busy: 'Thinking', bad: 'Offline' };

function HeaderButton({ label, onClick, children, on }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="cp-icon-btn" onClick={onClick} aria-label={label} data-on={on ? 'true' : undefined}>
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-[11px]">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(Boolean(mq.matches));
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

function useOnline() {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

// "See plans" works with or without a Router above the dock.
const PLANS_BTN = 'cp-orb cp-focus inline-flex h-9 items-center gap-1.5 px-4 text-xs font-semibold no-underline';

function RouterSeePlans() {
  const navigate = useNavigate();
  return (
    <button type="button" className={PLANS_BTN} onClick={() => navigate(PLANS_PATH)}>
      See plans
    </button>
  );
}

function SeePlansButton() {
  const inRouter = useInRouterContext();
  if (inRouter) return <RouterSeePlans />;
  return (
    <a href={PLANS_PATH} className={PLANS_BTN}>
      See plans
    </a>
  );
}

function PlanGateCard() {
  return (
    <div className="cp-card cp-fade-up flex flex-col items-center gap-2 px-4 py-5 text-center" data-testid="copilot-plan-gate" role="status">
      <span className="cp-gradient-soft cp-text-brand inline-flex h-10 w-10 items-center justify-center rounded-full" aria-hidden="true">
        <Lock className="h-5 w-5" />
      </span>
      <div className="text-[13px] font-semibold leading-5">The AI Assistant isn&apos;t in your plan</div>
      <p className="cp-muted max-w-[280px] text-[12px] leading-4">
        Upgrade to ask questions about applicants, compare candidates and quote their evidence.
      </p>
      <SeePlansButton />
    </div>
  );
}

function EmptyState({ suggestions, onPrompt, companyScope = false }) {
  const capabilities = companyScope ? COMPANY_CAPABILITIES : CAPABILITIES;
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-1 py-2 text-center" data-testid="copilot-empty">
      <CopilotAvatar size={40} className="cp-orb-ring" title="" aria-hidden="true" />
      <div>
        <h3 className="text-[14px] font-semibold leading-5">
          {companyScope ? 'Ask anything about hiring across your jobs' : "Ask anything about this role's applicants"}
        </h3>
        <p className="cp-muted mt-0.5 text-[12px] leading-4">
          {companyScope ? 'Answers cite the job, candidate and test they come from' : 'Answers cite the candidate and test they come from'}
        </p>
      </div>
      {suggestions && suggestions.length > 0 && (
        <div className="cp-stagger grid w-full grid-cols-1 gap-2 min-[420px]:grid-cols-2" data-testid="copilot-suggestions">
          {suggestions.map((s, i) => {
            const Icon = SUGGESTION_ICONS[s.icon] || Sparkles;
            return (
              <button
                key={`${s.text}-${i}`}
                type="button"
                style={{ '--i': i }}
                onClick={() => onPrompt(s.text)}
                className="cp-card cp-card-hover cp-gradient-border cp-focus flex min-h-[40px] items-center gap-2 px-3 py-2 text-left text-[12px] leading-4"
              >
                <span className="cp-gradient-soft cp-text-brand inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg" aria-hidden="true">
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0">{s.text}</span>
              </button>
            );
          })}
        </div>
      )}
      <ul className="cp-muted mt-1 space-y-1 text-[11px] leading-4" aria-label="What the AI Assistant can do">
        {capabilities.map(({ icon: Icon, text }) => (
          <li key={text} className="flex items-center justify-center gap-1.5">
            <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span>{text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select, [tabindex]:not([tabindex="-1"])';

export default function CopilotPanel({
  isMobile,
  mode,
  onSetMode,
  onMinimise,
  onClose,
  context,
  selectedName,
  onClearSelection,
  view,
  onShowHistory,
  onBackToChat,
  onNewChat,
  scope,
  scopes,
  currentJob,
  onPickScope,
  activeConversationId,
  onSelectConversation,
  onDeletedConversation,
  messages,
  inFlight,
  loadingHistory,
  suggestions,
  onPrompt,
  onRetry,
  onSpeak,
  onStopSpeak,
  speakingKey,
  isStreaming = false,
  hadNetworkError = false,
  planGated = false,
  composer,
}) {
  const panelRef = useRef(null);
  const listRef = useRef(null);
  const atBottomRef = useRef(true);
  const [newBelow, setNewBelow] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const online = useOnline();

  const scrollToBottom = useCallback(
    (force) => {
      const el = listRef.current;
      if (!el) return;
      if (!force && !atBottomRef.current) return;
      el.scrollTo({ top: el.scrollHeight, behavior: reducedMotion ? 'auto' : 'smooth' });
    },
    [reducedMotion]
  );

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    atBottomRef.current = atBottom;
    if (atBottom) setNewBelow(false);
  };

  // Auto-scroll on new content unless the user scrolled up — then offer a pill.
  useEffect(() => {
    if (atBottomRef.current) scrollToBottom(false);
    else if (messages.length || inFlight) setNewBelow(true);
  }, [messages, inFlight?.content, inFlight?.blocks?.length, inFlight?.statusLines?.length, inFlight, scrollToBottom]);

  // Focus trap in sheet (mobile) mode.
  useEffect(() => {
    if (!isMobile) return undefined;
    const node = panelRef.current;
    if (!node) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const focusables = Array.from(node.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    node.addEventListener('keydown', onKey);
    return () => node.removeEventListener('keydown', onKey);
  }, [isMobile]);

  const expanded = mode === 'expanded';
  const isEmpty = !messages.length && !inFlight && !loadingHistory;
  const tone = isStreaming ? 'busy' : !online || hadNetworkError ? 'bad' : 'ok';
  const ctx = context || { pageLabel: 'Dashboard', title: 'This job', count: 0, filters: [] };
  const jobScoped = scope?.type === 'job';

  return (
    <TooltipProvider delayDuration={300}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal={isMobile ? 'true' : undefined}
        aria-label="AI Assistant"
        data-testid="copilot-panel"
        data-mode={mode}
        className={cn(
          'fixed z-50 flex flex-col',
          isMobile
            ? 'inset-x-0 bottom-0 h-[88vh]'
            : cn(
                'bottom-24 right-6 h-[640px] max-h-[calc(100vh-7rem)]',
                expanded ? 'w-[760px] max-w-[calc(100vw-3rem)]' : 'w-[420px] max-w-[calc(100vw-3rem)]',
                !reducedMotion && 'transition-[width] duration-200'
              )
        )}
      >
        <div
          className={cn(
            'cp-glass cp-aura flex h-full min-h-0 w-full flex-col',
            isMobile ? 'cp-sheet-up rounded-t-[20px] border-b-0' : 'cp-panel-in rounded-[20px]'
          )}
        >
          {isMobile && <div className="cp-grab" aria-hidden="true" />}

          {/* Header */}
          <header className="flex flex-col gap-1.5 px-3 pb-2 pt-2.5" style={{ borderBottom: '1px solid var(--cp-glass-border)' }}>
            <div className="flex items-center gap-2">
              <CopilotAvatar size={32} className={cn('shrink-0', isStreaming && 'cp-think-ring')} title="" aria-hidden="true" />
              <div className="flex min-w-0 items-center gap-2">
                <span className="text-[13px] font-semibold leading-none">AI Assistant</span>
                <span className="cp-muted flex items-center gap-1.5 text-[11px] leading-none" title={STATUS_LABEL[tone]}>
                  <span className="cp-status-dot" data-tone={tone} data-testid="copilot-status-dot" aria-hidden="true" />
                  <span>{STATUS_LABEL[tone]}</span>
                </span>
              </div>
              <div className="ml-auto flex items-center gap-0.5">
                <HeaderButton label="New chat" onClick={onNewChat}>
                  <Plus className="h-4 w-4" />
                </HeaderButton>
                <HeaderButton label="History" on={view === 'history'} onClick={view === 'history' ? onBackToChat : onShowHistory}>
                  <History className="h-4 w-4" />
                </HeaderButton>
                {!isMobile && (
                  <HeaderButton label={expanded ? 'Collapse' : 'Expand'} onClick={() => onSetMode(expanded ? 'docked' : 'expanded')}>
                    {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  </HeaderButton>
                )}
                <HeaderButton label="Minimise" onClick={onMinimise}>
                  <Minus className="h-4 w-4" />
                </HeaderButton>
                <HeaderButton label="Close (Esc)" onClick={onClose}>
                  <X className="h-4 w-4" />
                </HeaderButton>
              </div>
            </div>

            {/* Context chips: page · scope switcher · (job scope) applicant count + filters */}
            <div className="cp-scroll flex items-center gap-1.5 overflow-x-auto pb-0.5" data-testid="copilot-context">
              {ctx.pageLabel && (
                <span className="cp-chip cp-chip-static max-w-[140px] shrink-0" title={`Page: ${ctx.pageLabel}`} data-testid="copilot-page-chip">
                  <LayoutDashboard className="h-3 w-3 shrink-0" aria-hidden="true" />
                  <span className="truncate">{ctx.pageLabel}</span>
                </span>
              )}
              <CopilotScopeSwitcher scope={scope} scopes={scopes} currentJob={currentJob} onPick={onPickScope} />
              {jobScoped && ctx.count !== null && ctx.count !== undefined && (
                <span className="cp-chip cp-chip-static cp-num shrink-0">{ctx.count} applicants</span>
              )}
              {jobScoped && (ctx.filters || []).map((f) => (
                <span key={f.key} className="cp-chip cp-chip-static shrink-0">
                  {f.label}
                </span>
              ))}
              {selectedName && (
                <span className="cp-chip cp-chip-static cp-chip-mint max-w-[220px] shrink-0" data-testid="copilot-selected-chip">
                  <User className="h-3 w-3 shrink-0" aria-hidden="true" />
                  <span className="truncate">Selected: {selectedName}</span>
                  {onClearSelection && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="cp-focus -mr-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full hover:bg-black/10 dark:hover:bg-white/10"
                          onClick={onClearSelection}
                          aria-label="Clear selection"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="text-[11px]">
                        Clear selection
                      </TooltipContent>
                    </Tooltip>
                  )}
                </span>
              )}
            </div>
          </header>

          {view === 'history' ? (
            <div className="cp-panel-in flex min-h-0 flex-1 flex-col" data-testid="copilot-history">
              <CopilotHistory
                scope={scope}
                jobs={scopes?.jobs}
                activeId={activeConversationId}
                onSelect={onSelectConversation}
                onBack={onBackToChat}
                onDeleted={onDeletedConversation}
              />
            </div>
          ) : (
            <>
              {/* Body */}
              <div className="relative min-h-0 flex-1">
                <div ref={listRef} onScroll={onScroll} className="cp-scroll h-full space-y-3 overflow-y-auto px-3 py-3">
                  {!online && (
                    <div className="cp-banner cp-banner-warn" role="status" data-testid="copilot-offline">
                      <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      <span>You&apos;re offline — answers will resume when the connection returns</span>
                    </div>
                  )}

                  {planGated && <PlanGateCard />}

                  {loadingHistory && (
                    <div className="space-y-3" aria-busy="true" aria-label="Loading conversation">
                      <div className="cp-shimmer ml-auto h-7 w-2/3" />
                      <div className="cp-shimmer h-16 w-11/12" />
                      <div className="cp-shimmer ml-auto h-7 w-1/2" />
                      <div className="cp-shimmer h-24 w-11/12" />
                    </div>
                  )}

                  {isEmpty && !planGated && (
                    <EmptyState suggestions={fallbackSuggestions(ctx.page, suggestions)} onPrompt={onPrompt} companyScope={!jobScoped} />
                  )}

                  {messages.map((m) =>
                    m.system ? (
                      <div key={m.key || m.id} className="cp-system-note cp-fade-up" role="status" data-testid="copilot-system-note">
                        {m.content}
                      </div>
                    ) : (
                    <CopilotMessage
                      key={m.key || m.id || m.created_at}
                      message={m}
                      onPrompt={onPrompt}
                      onRetry={m.error?.retryable ? () => onRetry(m) : undefined}
                      onSpeak={(text) => onSpeak(text, m.key)}
                      onStopSpeak={onStopSpeak}
                      speaking={speakingKey === m.key}
                    />
                    )
                  )}

                  {inFlight && (
                    <div aria-live="polite">
                      <CopilotMessage message={inFlight} onPrompt={onPrompt} />
                    </div>
                  )}
                </div>

                {newBelow && (
                  <button
                    type="button"
                    className="cp-chip cp-chip-mint cp-float-pill cp-fade-up"
                    onClick={() => {
                      setNewBelow(false);
                      scrollToBottom(true);
                    }}
                    data-testid="copilot-new-reply"
                  >
                    <ArrowDown className="h-3 w-3" aria-hidden="true" />
                    New reply
                  </button>
                )}
              </div>

              {/* Footer band */}
              <div
                className={cn('cp-glass-strong', isMobile ? '' : 'rounded-b-[20px]')}
                style={{ borderWidth: '1px 0 0 0', boxShadow: 'none' }}
              >
                <CopilotComposer ref={composer.inputRef} {...{ ...composer, inputRef: undefined }} />
              </div>
            </>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
